import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, X, Send, ExternalLink, Loader2, Mic, MicOff } from "lucide-react";

const SUGERENCIAS = [
  "¿Cuándo monté por última vez en Primor?",
  "¿Cuánto facturé en Sony este año?",
  "Resumen del cliente Zara",
  "¿Qué peticiones tengo pendientes de facturar?",
];

/** Burbuja flotante "Pregunta a la IA" abajo-derecha. */
export default function AIChatBubble() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [historial, setHistorial] = useState([]); // {rol, texto, sources}
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recogRef = useRef(null);
  const nav = useNavigate();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Detect Web Speech API
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSpeechSupported(true);
    const rec = new SR();
    rec.lang = "es-ES";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setMsg(transcript);
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      // Errores comunes: not-allowed, no-speech, audio-capture
      if (e.error && e.error !== "no-speech") {
        // eslint-disable-next-line no-console
        console.warn("SpeechRecognition error:", e.error);
      }
    };
    recogRef.current = rec;
    return () => {
      try { rec.abort(); } catch (_e) { /* ignore */ }
    };
  }, []);

  const toggleListen = useCallback(() => {
    if (!recogRef.current) return;
    if (listening) {
      try { recogRef.current.stop(); } catch (_e) { /* ignore */ }
      setListening(false);
    } else {
      setMsg("");
      try {
        recogRef.current.start();
        setListening(true);
      } catch (_e) { /* already running */ }
    }
  }, [listening]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [historial, loading]);

  const enviar = async (texto) => {
    const pregunta = (texto || msg).trim();
    if (!pregunta || loading) return;
    setHistorial((h) => [...h, { rol: "user", texto: pregunta }]);
    setMsg("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { pregunta }, { timeout: 60000 });
      setHistorial((h) => [...h, {
        rol: "ai",
        texto: data.respuesta || "(sin respuesta)",
        sources: data.sources || [],
        meta: {
          n_pet: data.n_peticiones_contexto,
          n_cli: data.n_clientes_contexto,
          n_esp: data.n_espacios_contexto,
        },
      }]);
    } catch (e) {
      setHistorial((h) => [...h, {
        rol: "ai",
        texto: `❌ Error: ${e?.response?.data?.detail || e.message}`,
        sources: [],
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="open-ai-bubble"
        title="Pregúntale a la IA"
        className="fixed bottom-24 right-5 z-50 flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-violet-500 to-fuchsia-600 hover:from-violet-400 hover:to-fuchsia-500 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all md:bottom-24"
        style={{ boxShadow: "0 0 24px rgba(168, 85, 247, 0.4)" }}
      >
        <Sparkles className="w-5 h-5" />
        <span className="text-sm font-semibold hidden md:inline">Pregunta IA</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-24 right-5 z-50 w-[92vw] md:w-[440px] max-w-[460px] bg-zinc-950 border border-violet-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      style={{ maxHeight: "min(75vh, 700px)" }}
      data-testid="ai-bubble">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-violet-500/15 to-fuchsia-500/10 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-100">IA RotulaciónDNF</div>
          <div className="text-[10px] text-violet-300/70">Pregúntame sobre tus datos · Powered by Gemini</div>
        </div>
        <button onClick={() => setOpen(false)} data-testid="close-ai" className="text-zinc-400 hover:text-zinc-100 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Chat */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm">
        {historial.length === 0 && (
          <div className="text-zinc-400 text-xs space-y-3 py-2">
            <div className="text-zinc-300">
              👋 Hola Dani. Pregúntame cualquier cosa sobre tus clientes, peticiones, gastos o presupuestos.
            </div>
            <div className="text-[11px] text-zinc-500">Ejemplos:</div>
            <div className="space-y-1.5">
              {SUGERENCIAS.map((s, i) => (
                <button key={i}
                  onClick={() => enviar(s)}
                  data-testid={`sugerencia-${i}`}
                  className="block text-left w-full text-xs px-3 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-violet-500/40 rounded-lg text-zinc-300 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {historial.map((m, i) => (
          <div key={i} className={`flex ${m.rol === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] rounded-xl px-3 py-2.5 ${
              m.rol === "user"
                ? "bg-amber-500/20 border border-amber-500/30 text-amber-100"
                : "bg-zinc-900 border border-zinc-800 text-zinc-100"
            }`} data-testid={`msg-${i}`}>
              <div className="whitespace-pre-wrap text-[13px] leading-relaxed">{m.texto}</div>
              {m.sources && m.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-zinc-800 space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Fuentes</div>
                  <div className="flex flex-wrap gap-1.5">
                    {m.sources.map((s, j) => (
                      <button
                        key={j}
                        onClick={() => { setOpen(false); nav(`/peticiones/${s.id}`); }}
                        data-testid={`source-${i}-${j}`}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 bg-violet-500/10 hover:bg-violet-500/20 text-violet-200 border border-violet-500/30 rounded-md transition-colors"
                      >
                        {s.label}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {m.meta && (
                <div className="mt-1.5 text-[9px] text-zinc-600">
                  Contexto: {m.meta.n_pet} peticiones · {m.meta.n_cli} clientes · {m.meta.n_esp} espacios
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-zinc-400 text-[13px] flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Buscando datos y consultando IA…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-zinc-800 p-3">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); } }}
            placeholder={listening ? "Escuchando…" : "Pregunta lo que quieras… o pulsa 🎙"}
            disabled={loading}
            data-testid="ai-input"
            className={`flex-1 bg-zinc-900 border rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none focus:border-violet-500/50 ${
              listening ? "border-red-500/60 animate-pulse" : "border-zinc-800"
            }`}
          />
          {speechSupported && (
            <button
              onClick={toggleListen}
              disabled={loading}
              data-testid="ai-mic"
              title={listening ? "Detener" : "Dictar pregunta"}
              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                listening
                  ? "bg-red-500 hover:bg-red-600 text-white animate-pulse"
                  : "bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800"
              }`}
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={() => enviar()}
            disabled={loading || !msg.trim()}
            data-testid="ai-send"
            className="px-3 py-2 bg-gradient-to-br from-violet-500 to-fuchsia-600 hover:from-violet-400 hover:to-fuchsia-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        {!speechSupported && (
          <div className="text-[10px] text-zinc-600 mt-1.5 text-center">
            (Dictado por voz no disponible en este navegador. Usa Chrome o Edge en móvil para activarlo.)
          </div>
        )}
      </div>
    </div>
  );
}
